import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import DraggableObject from "./DraggableObject";
import { SlidingDoorType, WallType } from "../../types/floorplannerTypes";
import { SelectableSymbol, BoundingBox } from './SelectableSymbol';
import { projectToWorld } from "./projectToWorld";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import AttachableSymbol from "./AttachableSymbol";
import { Line } from "@react-three/drei";
import { transaction } from "mobx";
import { editorStore } from "../../store/editorStore";
import { observer } from "mobx-react-lite";
import { AttachmentMeasurementLines } from "./AttachmentMeasurementLines";
import { getSymbolRenderTotalWidth } from "../../utils/symbolAttachmentGeometry";
import {
  getWallAttachmentRenderOrder,
  getWallAttachmentZOffset,
  usePlanarWallAttachmentLayer,
} from "./wallAttachmentRenderLayer";
import { CANVAS_ACCENT_COLOR, canvasAccentColor, canvasHandleFillColor } from "../../constants/brandColors";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../utils/opacityRemount";
import ObjectOpacityGroup from "./ObjectOpacityGroup";

export const defaultSlideAmount = 0;
export const defaultDoorWidth = 1;

interface SlidingDoorProps {
  door: SlidingDoorType;
  attachedToWall?: WallType;
  onDragStart: (door: SlidingDoorType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

const SlidingDoor: React.FC<SlidingDoorProps> = observer(({
  door,
  attachedToWall,
  onDragStart,
  onDragEnd,
  onDrag,
}) => {
  // Using openAngle or a dedicated slide property if available. We'll interpret openAngle as a 0-1 slide percentage if necessary, or just use a new slideAmount property.
  const [slideAmount, setSlideAmount] = useState(door.openAngle ?? defaultSlideAmount); 
  const slideAmountRef = useRef(slideAmount);
  const { gl, camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const wallAttachmentLayerRef = useRef<THREE.Group>(null);
  const attachableRef = useRef<{ checkForAttachment: () => void }>(null);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);
  const isAttachedToWall = Boolean(attachedToWall);
  const wallAttachmentRenderOrder = getWallAttachmentRenderOrder(door.zIndex);

  usePlanarWallAttachmentLayer(
    wallAttachmentLayerRef,
    isAttachedToWall,
    wallAttachmentRenderOrder,
  );

  const setWallAttachmentLayerRef = useCallback((node: THREE.Group | null) => {
    groupRef.current = node;
    wallAttachmentLayerRef.current = node;
  }, []);

  const lineWeight = door.lineWeight || floorplannerStore.doorLineWeight;
  const frameThickness = door.doorFrameWidth || floorplannerStore.doorFrameWidth;
  const bladeThickness = door.doorBladeThickness || floorplannerStore.doorBladeThickness;
  const [doorWidth, setDoorWidth] = useState(door.doorWidth);
  const grabHandleSize = 0.12;
  const handleRadius = useMemo(() => grabHandleSize / editorStore.zoomLevelDivisor(), [editorStore.zoomLevel]);
  const handleDepth = 0.02;

  const flipX = door.flipHorizontal ? -1 : 1;
  const flipY = door.flipVertical ? -1 : 1;
  const [centerPosition, setCenterPosition] = useState<[number, number]>([
    doorWidth / 2,
    doorWidth / 2,
  ]);
  const lineColor = door.lineColor || floorplannerStore.lineColor;
  const wallWidth = attachedToWall?.wallWidth || floorplannerStore.wallWidth;
  const wallLineWeight = floorplannerStore.convertLineWeightToWorld(attachedToWall?.lineWeight || floorplannerStore.lineWeight);
  const attachment = attachedToWall?.symbolAttachments?.find((item) => item.symbolId === door.id);
  
  const renderedClearOpening = useMemo(() => {
    if (!attachedToWall || !attachment) {
      // Unattached: doorWidth is the leaf; clear opening is leaf + passage beside it.
      return doorWidth * 2;
    }
    const openingWidth = getSymbolRenderTotalWidth(
      attachedToWall,
      door,
      attachment.positionFromStart,
      floorplannerStore.doorFrameWidth,
    );
    return Math.max(openingWidth - frameThickness * 2, 0.01);
  }, [attachedToWall, attachment?.positionFromStart, door, doorWidth, floorplannerStore.doorFrameWidth, frameThickness]);

  /** Leaf covers half the clear opening; the other half is the visible passage. */
  const renderedLeafWidth = renderedClearOpening / 2;

  // Base positions for sliding mechanics (origin at the closed-side jamb).
  const trackPositionX = door.flipHorizontal ? renderedClearOpening : 0;

  // Slide moves the leaf across the opening (0 = closed over near half, 1 = open over far half).
  const slideOffsetX = (slideAmount * renderedLeafWidth) * flipX;

  // Handle position is anchored to the center of the sliding blade
  const slideHandleX = trackPositionX + (renderedLeafWidth / 2 * flipX) + slideOffsetX;
  const slideHandleY = (bladeThickness * 2) * flipY;

  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingSlide, setIsDraggingSlide] = useState(false);
  const [hoveringHandle, setHoveringHandle] = useState<string | null>(null);
  const noopRaycast = () => null;
  const aboutToModify = useRef(false);
  const selectionHandlesVisible = editorStore.shouldShowSelectionHandles();

  const calculateBoundingBox = useCallback((): BoundingBox => {
    if (!groupRef.current) return { topLeft: [0, 0], topRight: [0, 0], bottomLeft: [0, 0], bottomRight: [0, 0], width: 0, height: 0, depth: 0 };

    // Full clear opening (leaf + passage); leaf slides within this span.
    let minX = 0;
    let maxX = renderedClearOpening;

    let minY = -bladeThickness * 2;
    let maxY = bladeThickness * 2;

    if (attachedToWall) {
      minX -= frameThickness + wallLineWeight;
      maxX += frameThickness + wallLineWeight;
      const halfWallWidth = wallWidth / 2 + wallLineWeight;
      minY = Math.min(minY, -halfWallWidth);
      maxY = Math.max(maxY, halfWallWidth);
    }

    const width = maxX - minX;
    const height = maxY - minY;

    setCenterPosition([(minX + maxX) / 2, (minY + maxY) / 2]);

    return {
      topLeft: [minX, maxY],
      topRight: [maxX, maxY],
      bottomLeft: [minX, minY],
      bottomRight: [maxX, minY],
      width: width,
      height: height,
      depth: 0,
    };
  }, [attachedToWall, bladeThickness, frameThickness, renderedClearOpening, wallLineWeight, wallWidth]);

  const dragOffset = useRef({ x: 0, y: 0 });

  const onHandleDrag = (event: PointerEvent) => {
    if (aboutToModify.current) {
      floorplannerStore.beginUndoTransaction('updateSlideAmount');
      floorplannerStore.recordSymbolForUndo(door.id);
      aboutToModify.current = false;
    }
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const doorRotation = door.rotation || 0;
    const mousePosition = new THREE.Vector2(worldX, worldY);
    const doorPosition = new THREE.Vector2(door.position.x, door.position.y);

    const offset = mousePosition.clone().sub(doorPosition);
    offset.rotateAround(new THREE.Vector2(0, 0), -doorRotation);

    const hingeX = door.flipHorizontal ? renderedClearOpening : 0;

    // Local X relative to the leaf's closed-center, then convert to 0–1 slide fraction.
    const localDragX = offset.x - hingeX - (renderedLeafWidth / 2 * flipX);
    let amount = (localDragX * flipX) / renderedLeafWidth;

    // Clamp between 0 (closed) and 1 (fully open)
    amount = Math.max(0, Math.min(1, amount));

    setSlideAmount(amount);
  };

  const onPointerDownHandle = (event: React.PointerEvent) => {
    event.stopPropagation();
    aboutToModify.current = true;
    setIsDraggingSlide(true);
    
    gl.domElement.addEventListener("pointermove", onHandleDrag);
    gl.domElement.addEventListener("pointerup", onPointerUpHandle);
    floorplannerStore.setBlockDirty(true);
  };

  const onPointerUpHandle = () => {
    aboutToModify.current = false;
    setIsDraggingSlide(false);
    if (door.openAngle !== slideAmountRef.current) { // Adapting property if needed
      floorplannerStore.updateSymbolProperty(door.id, "openAngle", slideAmountRef.current);
      floorplannerStore.recordSymbolChange(door.id, 'modify');
      floorplannerStore.pushToUndoStack();
    }
    gl.domElement.removeEventListener("pointermove", onHandleDrag);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandle);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.setDirty();
  };

  const handleDrag = (newPosition: [number, number]) => {
    onDrag(newPosition);
  };

  // Leaf sits slightly off the wall centerline (surface-mounted sliding leaf).
  const bladeYOffset = bladeThickness * flipY;
  const doorBladePoints = useMemo(() => [
    [0, bladeYOffset - bladeThickness, 0] as [number, number, number],
    [renderedLeafWidth * flipX, bladeYOffset - bladeThickness, 0] as [number, number, number],
    [renderedLeafWidth * flipX, bladeYOffset + bladeThickness, 0] as [number, number, number],
    [0, bladeYOffset + bladeThickness, 0] as [number, number, number],
    [0, bladeYOffset - bladeThickness, 0] as [number, number, number],
  ], [bladeThickness, bladeYOffset, flipX, renderedLeafWidth]);

  /**
   * Dashed travel path for the clear half of the opening (beside the closed leaf).
   */
  const doorTrackPoints = useMemo(() => [
    [renderedLeafWidth * flipX, bladeYOffset, 0] as [number, number, number],
    [renderedClearOpening * flipX, bladeYOffset, 0] as [number, number, number],
  ], [bladeYOffset, flipX, renderedClearOpening, renderedLeafWidth]);

  const generateFramePoints = useCallback((wallWidth: number, side: number): [number, number, number][] => {
    const offset = (side * (renderedClearOpening / 2) + renderedClearOpening / 2);
    return [
      [offset, -wallWidth / 2, 0],
      [offset + frameThickness * side, -wallWidth / 2, 0],
      [offset + frameThickness * side, wallWidth / 2, 0],
      [offset, wallWidth / 2, 0],
      [offset, -wallWidth / 2, 0],
    ];
  }, [frameThickness, renderedClearOpening]);

  const handleResize = (newWidth: number, newHeight: number, boundingBox: BoundingBox, handle: string) => {
    // Bbox spans the full opening (~2 × doorWidth); map half of that delta onto the leaf.
    const widthDelta = newWidth - boundingBox.width;
    const panelWidthDelta = widthDelta / 2;
    const newDoorWidth = Math.max(doorWidth + panelWidthDelta, 0.1);
    const widthDiff = newDoorWidth - door.doorWidth;

    // Origin is the left jamb (same as double doors). Left handles keep the right jamb fixed.
    const isLeftHandle = handle === "topLeft" || handle === "bottomLeft";
    let positionDeltaX = 0;
    if (isLeftHandle) {
      positionDeltaX = -widthDiff * 2;
    }

    transaction(() => {
      floorplannerStore.updateSymbolProperty(door.id, "doorWidth", newDoorWidth);
      if (positionDeltaX !== 0) {
        const position = [
          door.position.x + positionDeltaX * Math.cos(door.rotation || 0),
          door.position.y + positionDeltaX * Math.sin(door.rotation || 0),
        ];
        if (attachedToWall) {
          floorplannerStore.detachSymbolFromWall(door.id);
          floorplannerStore.updateSymbolProperty(door.id, "position", position);
          floorplannerStore.attachSymbolToWall(door.id, attachedToWall.id, [position[0], position[1]]);
        } else {
          floorplannerStore.updateSymbolProperty(door.id, "position", position);
        }
      }
    });
  }

  const handleAttachments = () => {}

  const handlePointerOver = (handle: string) => setHoveringHandle(handle);
  const handlePointerOut = () => setHoveringHandle(null);

  useEffect(() => setSlideAmount(door.openAngle ?? defaultSlideAmount), [door.openAngle]);
  useEffect(() => setDoorWidth(door.doorWidth), [door.doorWidth]);
  useEffect(() => { slideAmountRef.current = slideAmount; }, [slideAmount]);
  useEffect(() => { editorStore.updateGroupRef(door.id, groupRef.current); }, [groupRef.current]);

  return (
    <group position={[0, 0, getWallAttachmentZOffset(isAttachedToWall, door.zIndex)]}>
      {attachedToWall && (
        <AttachmentMeasurementLines
          symbol={door}
          attachedWall={attachedToWall}
          isDragging={isDragging}
        />
      )}
      <DraggableObject
        position={[door.position.x, door.position.y]}
        onDragStart={(offset) => {
          setIsDragging(true);
          onDragStart(door, offset)
        }}
        onDragEnd={(endPosition) => {
          onDragEnd(endPosition)
          setIsDragging(false);
        }}
        onDrag={handleDrag}
        selectable={true}
        attachmentId={door.id}
        attachmentType="doorAttachments"
        symbol={door}
      >
        <AttachableSymbol
          attachmentId={door.id}
          attachmentType="doorAttachments"
          onAttachment={handleAttachments}
          store={floorplannerStore}
          ref={attachableRef}
        >
          <SelectableSymbol
            ref={groupRef}
            handleSize={floorplannerStore.symbolHandleSize}
            calculateBoundingBox={calculateBoundingBox}
            onResizeStart={() => setIsResizing(true)}
            onResize={handleResize}
            onResizeEnd={() => setIsResizing(false)}
            center={centerPosition}
            rotation={door.rotation}
            symbol={door}
            onPointerOver={() => handlePointerOver('symbol')}
            onPointerOut={handlePointerOut}
            isDragging={isDragging}
          >
            <group
              ref={setWallAttachmentLayerRef}
              rotation={[0, 0, door.rotation || 0]}
              renderOrder={isAttachedToWall ? wallAttachmentRenderOrder : undefined}
              onPointerEnter={() => {
                if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
              }}
              onPointerLeave={() => {
                if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
              }}
            >
              <mesh
                position={[
                  trackPositionX + renderedClearOpening * flipX / 2,
                  bladeYOffset,
                  -0.03
                ]}
                rotation={[0, 0, 0]}
              >
                {/* Cover full clear opening (leaf + passage) for hit testing. */}
                <boxGeometry args={[renderedClearOpening, Math.max(bladeThickness * 4, 0.2), 0.06]} />
                <meshBasicMaterial transparent opacity={0.0} />
              </mesh>

              <ObjectOpacityGroup
                key={`sliding-door-opacity-${Math.round(resolveFillOpacity(door) * 100)}-${getOpacityRemountEpoch(door.id)}`}
                opacity={resolveFillOpacity(door)}
              >
              {/* Pocket / travel path (dashed) — outside the leaf only */}
              <Line
                points={doorTrackPoints}
                color={lineColor}
                lineWidth={lineWeight / 3}
                position={new THREE.Vector3(trackPositionX, 0, 0)}
                dashed={true}
                dashSize={0.15}
                gapSize={0.1}
                raycast={noopRaycast}
              />

              {/* Sliding door leaf (solid outline) */}
              <Line
                points={doorBladePoints}
                color={canvasAccentColor(door.selected, hoveringHandle === 'symbol', lineColor)}
                lineWidth={lineWeight}
                position={new THREE.Vector3(trackPositionX + slideOffsetX, 0, 0)}
                {...(door.lineType === "dashed" && {
                  dashed: true,
                  dashSize: 0.15,
                  gapSize: 0.1,
                })}
                raycast={noopRaycast}
              />

              {attachedToWall && (
                <>
                  <Line
                    points={generateFramePoints(wallWidth, -1)}
                    color={canvasAccentColor(door.selected, hoveringHandle === 'symbol', lineColor)}
                    lineWidth={1}
                    raycast={noopRaycast}
                  />
                  <Line
                    points={generateFramePoints(wallWidth, 1)}
                    color={canvasAccentColor(door.selected, hoveringHandle === 'symbol', lineColor)}
                    lineWidth={1}
                    raycast={noopRaycast}
                  />
                </>
              )}
              </ObjectOpacityGroup>

              {/* Sliding Adjustment Handle */}
              {(selectionHandlesVisible && door.selected && !isDragging && !isResizing && editorStore.selections.length === 1) && (
                <group
                  onPointerDown={onPointerDownHandle as unknown as (event: THREE.Event) => void}
                  onPointerUp={onPointerUpHandle as unknown as (event: THREE.Event) => void}
                  onPointerOver={() => handlePointerOver('slideHandle')}
                  onPointerOut={handlePointerOut}
                  position={[
                    slideHandleX,
                    slideHandleY,
                    0,
                  ]}
                >
                  <mesh>
                    <torusGeometry args={[(handleRadius - 0.002), 0.005, 16, 100]} />
                    <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                  </mesh>
                  <mesh
                    position={[0, 0, handleDepth / 2]}
                    rotation={[Math.PI / 2, 0, 0]}
                  >
                    <cylinderGeometry args={[(handleRadius - 0.02), (handleRadius - 0.006), handleDepth, 32]} />
                    <meshBasicMaterial color={canvasHandleFillColor(isDraggingSlide, hoveringHandle === "slideHandle")} />
                  </mesh>
                </group>
              )}
            </group>
          </SelectableSymbol>
        </AttachableSymbol>
      </DraggableObject>
    </group>
  );
});

export default SlidingDoor;